import { useCallback, useState } from "react";
import styled from "styled-components";
import shallow from "zustand/shallow";
import { ReactComponent as Megaphone } from "@c1/gravity-icons/dist/svg/ui-filled-megaphone-1-24.svg";
import usePageTitle from "../../../utils/hooks/usePageTitle";
import Heading from "../../shared/Heading";
import { CenteredRow, Col, Grid } from "../../shared/Layout";
import HiddenAlert from "../../shared/HiddenAlert";
import Page from "../../shared/Page";
import Banner from "../../shared/Banner";
import useStore from "../../../store/store";
import RadioList from "../../shared/RadioList";
import {
  SOLE_PROPRIETORSHIP,
  LIMITED_LIABILITY_COMPANY,
  PARTNERSHIP,
  CORPORATION,
  OTHER,
  convertLegalEntityEnumToDisplay,
} from "../../../constants/legalEntityTypes";
import OtherBusinessFindBranchInfo from "./OtherBusinessFindBranchInfo";
import CorporationTaxInfoForm from "./CorporationTaxInfoForm";
import PartnershipTaxInfoForm from "./PartnershipTaxInfoForm";
import SolePropTaxInfoForm from "./SolePropTaxInfoForm";
import SingleMemberLLCTaxInfoForm from "./SingleMemberLLCTaxInfoForm";
import LLCTaxInfoForm from "./LLCTaxInfoForm";
import usePreventNavigation from "../../../utils/hooks/usePreventNavigation";
import useFocusHeading from "../../../utils/hooks/useFocusHeading";
import { INDIVIDUAL_SOLE_PROPRIETOR_OR_SINGLE_MEMBER_LLC } from "../../../constants/businessTaxClassifications";

const taxInfoFormLookup = {
  [SOLE_PROPRIETORSHIP]: SolePropTaxInfoForm,
  [INDIVIDUAL_SOLE_PROPRIETOR_OR_SINGLE_MEMBER_LLC]: SingleMemberLLCTaxInfoForm,
  [LIMITED_LIABILITY_COMPANY]: LLCTaxInfoForm,
  [PARTNERSHIP]: PartnershipTaxInfoForm,
  [CORPORATION]: CorporationTaxInfoForm,
  [OTHER]: OtherBusinessFindBranchInfo,
};

const RadioListCol = styled(Col)`
  margin-top: var(--grv-size-spacing-large-3);
`;

const LegalEntity = () => {
  usePageTitle("Company Type");
  usePreventNavigation();
  const headingRef = useFocusHeading();

  const [storedCompanyType, storedIsSingleMemberLLC, isApplicantPrefilled, hasNoEligibleExistingBusinesses] =
    useStore(
      state => [
        state.companyType,
        state.isSingleMemberLLC,
        state.isApplicantPrefilled,
        state.existingCustomerEligibleBusinesses.length === 0,
      ],
      shallow
    );

  const [companyTypeSelected, setCompanyTypeSelected] = useState(
    // for SMLLC, navigating back to this page needs to re-select SMLLC
    storedIsSingleMemberLLC ? INDIVIDUAL_SOLE_PROPRIETOR_OR_SINGLE_MEMBER_LLC : storedCompanyType
  );

  const onLegalEntitySelectionChange = useCallback(({ target }) => {
    setCompanyTypeSelected(target.value);
  }, []);

  const displayBusinessTaxInfoSection = companyTypeSelected !== "";
  const displayHiddenAlert = companyTypeSelected !== "" && companyTypeSelected !== OTHER;
  const TaxInfoSection = taxInfoFormLookup[companyTypeSelected];

  const radioGroupLabel = "What type of company do you have?";

  return (
    <Page>
      {displayHiddenAlert && (
        <HiddenAlert>
          Fill out the associated form fields below for your{" "}
          {convertLegalEntityEnumToDisplay(companyTypeSelected)}
        </HiddenAlert>
      )}
      <Grid ref={headingRef}>
        <Heading
          step="SECTION 2 OF 4"
          mainHeading={radioGroupLabel}
          subHeading={
            isApplicantPrefilled && hasNoEligibleExistingBusinesses
              ? null
              : "Select your company type to view account opening options."
          }
          margin="large"
        />
        {isApplicantPrefilled && hasNoEligibleExistingBusinesses && (
          <CenteredRow className="grv-margin__top--large-3">
            <Col lg={8} md={8} sm={4}>
              <Banner
                bannerText="There are no eligible businesses associated with your personal account. Select a company type to open a bank account for a new business."
                bannerIcon={<Megaphone alt="" role="presentation" />}
              />
            </Col>
          </CenteredRow>
        )}
      </Grid>
      <Grid>
        <CenteredRow>
          <RadioListCol lg={8} md={8} sm={4}>
            <RadioList
              aria-label={radioGroupLabel}
              value={companyTypeSelected}
              id="businessLegalEntityTypeSelected"
              name="businessLegalEntityTypeSelected"
              radios={[
                {
                  id: "companyType_businessLegalEntityType_Radio_SoleProprietorship",
                  label: convertLegalEntityEnumToDisplay(SOLE_PROPRIETORSHIP),
                  value: SOLE_PROPRIETORSHIP,
                },
                {
                  id: "companyType_businessLegalEntityType_Radio_SingleMemberLimitedLiabilityCompany",
                  label: "Single Member LLC",
                  value: INDIVIDUAL_SOLE_PROPRIETOR_OR_SINGLE_MEMBER_LLC,
                },
                {
                  id: "companyType_businessLegalEntityType_Radio_LimitedLiabilityCompany",
                  label: "Multi Member LLC",
                  value: LIMITED_LIABILITY_COMPANY,
                },
                {
                  id: "companyType_businessLegalEntityType_Radio_Partnership",
                  label: convertLegalEntityEnumToDisplay(PARTNERSHIP),
                  value: PARTNERSHIP,
                },
                {
                  id: "companyType_businessLegalEntityType_Radio_Corporation",
                  label: convertLegalEntityEnumToDisplay(CORPORATION),
                  value: CORPORATION,
                },
                {
                  id: "companyType_businessLegalEntityType_Radio_SomethingElse",
                  label: convertLegalEntityEnumToDisplay(OTHER),
                  value: OTHER,
                },
              ]}
              onChange={onLegalEntitySelectionChange}
            />
          </RadioListCol>
        </CenteredRow>
      </Grid>
      {displayBusinessTaxInfoSection && TaxInfoSection && <TaxInfoSection />}
    </Page>
  );
};

export default LegalEntity;
